import {
  IAbbreviationWirePayload,
  IAbbreviationWireTime,
  IGeneralThemeNumberFormat,
  IGeneralThemeQuantityFormat,
  IGeneralThemeTimeFormat
} from 'components/Theme/GeneralSettings/generalTheme.types';

import {
  EOfferFormatKey,
  ETimeAbbreviationStyle,
  ETimeBaseUnit,
  INumberFormat,
  IQuantityFormat,
  ITimeFormat
} from '../../StoreSettings.types';

import {
  BASE_UNIT_THRESHOLD_VALUE,
  DEFAULT_TIME_BASE_UNIT,
  TIME_BASE_UNIT_TO_PAYLOAD,
  TIME_DESCRIPTOR_BY_STYLE,
  TIME_PRESENTATION_FORMAT,
  TIME_UNIT_VALUES
} from './AbbreviationsSettingsV2.consts';

type WireOfferKey = Exclude<keyof IAbbreviationWirePayload, 'time'>;

const OFFER_KEY_TO_WIRE: Record<EOfferFormatKey, WireOfferKey> = {
  [EOfferFormatKey.PRODUCTS]: 'bundle',
  [EOfferFormatKey.ROLLING_OFFER]: 'rollingOffer',
  [EOfferFormatKey.TRIGGERED_POPUP]: 'triggeredPopups',
  [EOfferFormatKey.SPECIAL_OFFER]: 'specialOffer',
  [EOfferFormatKey.PROGRESS_BAR]: 'progressBar',
  [EOfferFormatKey.BALANCE]: 'balance'
};

const GLOBAL_TIME_SOURCE_OFFER = EOfferFormatKey.PRODUCTS;

const toWireQuantity = (
  quantity: IQuantityFormat
): IGeneralThemeQuantityFormat => ({ ...quantity });

const toWireTime = (time: ITimeFormat): IAbbreviationWireTime => {
  const descriptors = TIME_DESCRIPTOR_BY_STYLE[time.abbreviationStyle];
  return {
    presentationFormat: TIME_PRESENTATION_FORMAT,
    baseUnitThreshold: {
      value: BASE_UNIT_THRESHOLD_VALUE,
      unit: TIME_BASE_UNIT_TO_PAYLOAD[DEFAULT_TIME_BASE_UNIT]
    },
    minute: { value: TIME_UNIT_VALUES.minute, descriptor: descriptors.minute },
    hour: { value: TIME_UNIT_VALUES.hour, descriptor: descriptors.hour },
    day: { value: TIME_UNIT_VALUES.day, descriptor: descriptors.day }
  };
};

export const toWirePayload = (
  numberFormat: INumberFormat
): IAbbreviationWirePayload =>
  Object.values(EOfferFormatKey).reduce<IAbbreviationWirePayload>(
    (accumulator, offer) => {
      accumulator[OFFER_KEY_TO_WIRE[offer]] = toWireQuantity(
        numberFormat[offer].quantity
      );
      return accumulator;
    },
    {
      time: toWireTime(numberFormat[GLOBAL_TIME_SOURCE_OFFER].time)
    }
  );

const fromPayloadAbbreviationStyle = (
  time: IAbbreviationWireTime | undefined
): ETimeAbbreviationStyle | undefined => {
  if (time === undefined) {
    return undefined;
  }
  const match = Object.entries(TIME_DESCRIPTOR_BY_STYLE).find(
    ([, descriptors]) => descriptors.minute === time.minute.descriptor
  );
  if (match === undefined) {
    return undefined;
  }
  return match[0] as ETimeAbbreviationStyle;
};

const fromPayloadBaseUnit = (unit: string): ETimeBaseUnit | undefined => {
  const match = Object.entries(TIME_BASE_UNIT_TO_PAYLOAD).find(
    ([, wireUnit]) => wireUnit === unit
  );
  if (match === undefined) {
    return undefined;
  }
  return match[0] as ETimeBaseUnit;
};

const fromWireTime = (
  time: IAbbreviationWireTime | undefined
): IGeneralThemeTimeFormat | undefined => {
  if (time === undefined) {
    return undefined;
  }
  const wireTime: IGeneralThemeTimeFormat = {};
  const abbreviationStyle = fromPayloadAbbreviationStyle(time);
  if (abbreviationStyle !== undefined) {
    wireTime.abbreviationStyle = abbreviationStyle;
  }
  if (time.baseUnitThreshold !== undefined) {
    wireTime.baseUnitThreshold = time.baseUnitThreshold.value;
    const baseUnit = fromPayloadBaseUnit(time.baseUnitThreshold.unit);
    if (baseUnit !== undefined) {
      wireTime.baseUnit = baseUnit;
    }
  }
  return Object.keys(wireTime).length > 0 ? wireTime : undefined;
};

export const fromWirePayload = (
  payload?: IAbbreviationWirePayload
): IGeneralThemeNumberFormat | undefined => {
  if (payload === undefined) {
    return undefined;
  }
  const time = fromWireTime(payload.time);

  return Object.values(EOfferFormatKey).reduce<IGeneralThemeNumberFormat>(
    (accumulator, offer) => {
      accumulator[offer] = {
        quantity: payload[OFFER_KEY_TO_WIRE[offer]],
        time
      };
      return accumulator;
    },
    {}
  );
};
